import type { HTMLProps } from 'react'
import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import classnames from 'classnames'
import type { Activity as ActivityInterface, ActivityDetails } from '@paintscout/api'
import type { ActivityType } from 'paintscout'
import { motion } from 'framer-motion'
import type { StyleClasses } from '@paintscout/ui/styles'

import ActivityAvatar from './ActivityAvatar'
import QuoteViewedActivity from './variants/QuoteViewedActivity'
import QuoteOwnerChangedActivity from './variants/QuoteOwnerChangedActivity'
import NoteActivity from './variants/NoteActivity'
import QuoteTypeChangedActivity from './variants/QuoteTypeChangedActivity'
import FollowUpSentActivity from './variants/FollowUpSentActivity'
import QuoteTagsChangedActivity from './variants/QuoteTagsChangedActivity'
import FileAddedActivity from './variants/FileAddedActivity'
import RecordMergedActivity from './variants/RecordMergedActivity'
import TaskActivity from './variants/TaskActivity'
import DealActivity from './variants/DealActivity'
import DealStageChangedActivity from './variants/DealStageChangedActivity'
import EventActivity from './variants/EventActivity'
import BasicQuoteActivity from './variants/BasicQuoteActivity'
import MessageActivity from './variants/MessageActivity'
import QuoteAcceptedActivity from './variants/QuoteAcceptedActivity'
import QuoteAssignmentActivity from './variants/QuoteAssignmentActivity'
import QuoteDeclinedActivity from './variants/QuoteDeclinedActivity'
import QuoteErrorActivity from './variants/QuoteErrorActivity'
import QuoteInvoicedActivity from './variants/QuoteInvoicedActivity'
import QuotePaymentActivity from './variants/QuotePaymentActivity'
import QuotePaymentRequestedActivity from './variants/QuotePaymentRequestedActivity'
import QuoteSentActivity from './variants/QuoteSentActivity'
import QuoteStatusChangedActivity from './variants/QuoteStatusChangedActivity'
import QuoteStatusUpdatedActivity from './variants/QuoteStatusUpdatedActivity'
import QuoteThankYouActivity from './variants/QuoteThankYouActivity'
import QuoteThankYouDeclinedActivity from './variants/QuoteThankYouDeclinedActivity'
import QuoteWarningActivity from './variants/QuoteWarningActivity'
import LeadFormSubmissionActivity from './variants/LeadFormSubmissionActivity'
import SettingsEditedActivity from './variants/SettingsEditedActivity'

export interface ActivityProps extends HTMLProps<HTMLDivElement> {
  classes?: StyleClasses<typeof useStyles>
  activity: ActivityInterface
  isQuote?: boolean
  /** Let chat message bubbles span the row instead of the quote sidebar's narrow cap. */
  wideMessages?: boolean
  showContent?: boolean
  showDocTypeDetails?: boolean
  animate?: boolean
  actions?: boolean
  disableLink?: boolean
  onAction?: (action: string, activity: ActivityInterface) => any
  layout?: boolean | 'position'

  /** Ref for the activity dot */
  dotRef?: React.Ref<SVGElement>
}

export interface ActivityTypeComponentProps<T extends ActivityDetails = ActivityDetails>
  extends Omit<ActivityProps, 'activityComponent' | 'activityDotComponent'> {
  activity: ActivityInterface & { details: T }
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      background: 'none',
      border: 'none',
      width: '100%',
      padding: theme.spacing()
    },
    dot: {},
    content: {
      textAlign: 'left',
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch'
    }
  }),
  {
    name: 'Activity'
  }
)

export default function Activity(props: ActivityProps) {
  const {
    activity,
    disabled,
    showContent,
    showDocTypeDetails,
    actions,
    onAction,
    isQuote,
    wideMessages,
    animate,
    layout,
    disableLink,
    className,
    ...divProps
  } = props
  const classes = useStyles(props)

  const ActivityTypeComponent = getComponentForType(activity.details?.activityType as ActivityType)

  if (!ActivityTypeComponent) {
    console.warn(`No component for activity type "${activity.details?.activityType}"`)
    return null
  }

  return (
    <motion.div layout={layout} {...(divProps as any)} className={classnames(classes.root, className)}>
      <ActivityAvatar activity={activity} />
      <motion.div className={classes.content} initial={animate && { opacity: 0 }} animate={animate && { opacity: 1 }}>
        <ActivityTypeComponent
          disabled={disabled}
          isQuote={isQuote}
          wideMessages={wideMessages}
          activity={activity}
          showContent={showContent}
          showDocTypeDetails={showDocTypeDetails}
          actions={actions}
          disableLink={disableLink}
          onAction={onAction}
        />
      </motion.div>
    </motion.div>
  )
}

function getComponentForType(type: ActivityType): React.ComponentType<ActivityTypeComponentProps<any>> {
  switch (type) {
    case 'created':
    case 'copied':
    case 'deleted':
    case 'disputed':
    case 'archived':
    case 'unarchived':
    case 'downloaded':
    case 'restored':
    case 'edited':
      return BasicQuoteActivity
    case 'deal-created':
    case 'deal-assigned':
      return DealActivity
    case 'deal-stage-changed':
      return DealStageChangedActivity
    case 'task-created':
    case 'task-assigned':
    case 'task-completed':
      return TaskActivity
    case 'event-created':
    case 'event-cancelled':
    case 'event-assigned':
    case 'event-progress-updated':
      return EventActivity
    case 'message':
      return MessageActivity
    case 'error':
      return QuoteErrorActivity
    case 'accepted':
      return QuoteAcceptedActivity
    case 'declined':
      return QuoteDeclinedActivity
    case 'invoiced':
      return QuoteInvoicedActivity
    case 'status-changed':
      return QuoteStatusChangedActivity
    case 'status-updated':
      return QuoteStatusUpdatedActivity
    case 'sent':
      return QuoteSentActivity
    case 'thank-you-sent':
      return QuoteThankYouActivity
    case 'declined-sent':
      return QuoteThankYouDeclinedActivity
    case 'payment':
      return QuotePaymentActivity
    case 'assignment':
      return QuoteAssignmentActivity
    case 'owner-changed':
      return QuoteOwnerChangedActivity
    case 'type-changed':
      return QuoteTypeChangedActivity
    case 'viewed':
      return QuoteViewedActivity
    case 'warning':
      return QuoteWarningActivity
    case 'payment-requested':
      return QuotePaymentRequestedActivity
    case 'payment-received-sent':
    case 'event-email-sent':
    case 'follow-up-sent':
      return FollowUpSentActivity
    case 'tags-changed':
      return QuoteTagsChangedActivity
    case 'note':
      return NoteActivity
    case 'file-added':
      return FileAddedActivity
    case 'record-merged':
      return RecordMergedActivity
    case 'lead-form-submission':
      return LeadFormSubmissionActivity
    case 'settings-edited':
      return SettingsEditedActivity
  }

  return null
}
